import { Loader2 } from "lucide-react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { type ConversationBrand, useCreateChatConversation } from "@/lib/api";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

// Constant-length JSX expression keeps prettier output stable for any rendered name.
const PROJECT_NAME = "Spare Parts ID";

/**
 * The brands a conversation can be pinned to. Kept in the same order as the
 * backend enum so the picker reads consistently with the API contract.
 */
const BRAND_OPTIONS: ConversationBrand[] = ["Wheelabrator", "DISA"];

/** Logo shown on each brand card, served from `public/brand/`. */
const BRAND_LOGOS: Record<ConversationBrand, string> = {
  Wheelabrator: "/brand/wheelabrator-logo.jpeg",
  DISA: "/brand/disa-logo.jpeg",
};

interface BrandCardProps {
  brand: ConversationBrand;
  /** True while this card's conversation is being created. */
  isPending: boolean;
  /** True while any card's conversation is being created. */
  isDisabled: boolean;
  onSelect: (brand: ConversationBrand) => void;
}

function BrandCard({ brand, isPending, isDisabled, onSelect }: BrandCardProps) {
  return (
    <Button
      variant="outline"
      disabled={isDisabled}
      onClick={() => onSelect(brand)}
      className={cn(
        "aspect-square h-auto w-full flex-col items-center justify-center gap-4 rounded-2xl border-[var(--color-border)] p-10",
        "hover:border-primary focus-visible:border-primary",
      )}
    >
      {/* Source logos are square (200x200), so the card is square too — lets
          the mark grow well past the old cropped-strip size. */}
      <img
        src={BRAND_LOGOS[brand]}
        alt={brand}
        className="h-40 w-auto max-w-full object-contain md:h-48"
      />
      {isPending && <Loader2 className="h-5 w-5 animate-spin" />}
    </Button>
  );
}

export function SelectBrandPage() {
  const navigate = useNavigate();
  const createConversation = useCreateChatConversation();
  // Which brand the in-flight request is for, so only that card spins.
  const [pendingBrand, setPendingBrand] = useState<ConversationBrand | null>(null);
  const [error, setError] = useState("");
  // Synchronous mirror of `pendingBrand` used as the create guard (see below).
  const isCreatingRef = useRef(false);

  const handleSelect = async (brand: ConversationBrand) => {
    // Two fast clicks would otherwise create two conversations: the button's
    // disabled state comes from React state / query flags, which only apply on
    // the next render, so the second click can reach `mutateAsync` first. The
    // ref flips synchronously inside this handler, so it blocks the second
    // click no matter when React re-renders. `pendingBrand` state is kept for
    // rendering (which card spins).
    if (isCreatingRef.current) {
      logger.debug("[select-brand]", "ignoring duplicate click", { brand });
      return;
    }
    isCreatingRef.current = true;
    setError("");
    setPendingBrand(brand);
    logger.debug("[select-brand]", "creating conversation", { brand });
    try {
      const created = await createConversation.mutateAsync({ brand });
      if (!created) {
        setError("Could not start a conversation. Please try again.");
        setPendingBrand(null);
        isCreatingRef.current = false;
        return;
      }
      logger.debug("[select-brand]", "created conversation", created.id);
      // The pending flags are deliberately left set on success: navigation
      // unmounts this page, and keeping the cards locked closes the window
      // where a click could create a second conversation.
      void navigate(`/chat/${created.id}`);
    } catch (err) {
      logger.error("[select-brand]", "failed to create conversation", err);
      setError("Could not start a conversation. Please try again.");
      setPendingBrand(null);
      isCreatingRef.current = false;
    }
  };

  // Disable as soon as a brand is picked — `pendingBrand` is set before the
  // request goes out, so it flips a render earlier than the query's own flag.
  const isCreating = pendingBrand !== null || createConversation.isPending;

  return (
    <div className="bg-background min-h-screen">
      {/* No back link: login lands straight here, so there's no previous page
          to return to. */}
      <header className="border-border bg-card/90 flex items-center border-b px-6 py-4 backdrop-blur">
        <div className="flex items-center gap-4">
          <img src="/brand/logo.png" alt="Norican" className="h-8 w-auto" />
          <div>
            <p className="font-heading text-foreground text-sm font-semibold">{PROJECT_NAME}</p>
            <p className="text-muted text-xs">AI spare parts identification workspace</p>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-6 py-12">
        <div className="max-w-2xl">
          <h1 className="font-heading text-foreground text-3xl font-semibold md:text-4xl">
            Spare Part ID Agent
          </h1>
          <p className="text-muted mt-2 text-sm leading-6">Select brand:</p>
        </div>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {BRAND_OPTIONS.map((brand) => (
            <BrandCard
              key={brand}
              brand={brand}
              isPending={pendingBrand === brand}
              isDisabled={isCreating}
              onSelect={handleSelect}
            />
          ))}
        </div>
        {error && <p className="text-destructive mt-6 text-sm">{error}</p>}
      </main>
    </div>
  );
}
